import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCrossTabSync } from './useCrossTabSync';
import { supabase } from '@/lib/supabase';

/**
 * Hook que detecta quando a aba volta ao foco após estar oculta
 * e força um refresh dos dados para evitar estado corrompido do Supabase.
 *
 * Agora é TAB-AWARE: não reseta cache se outras abas estiverem ativas.
 *
 * Baseado em issues conhecidas:
 * - https://github.com/supabase/supabase/issues/36046
 * - https://github.com/supabase/supabase-js/issues/1620
 *
 * O cliente Supabase pode entrar em estado corrompido quando a aba
 * é suspensa pelo navegador, causando queries que nunca completam.
 */

// ⚠️ EGRESS FIX: aumentado de 15 min → 60 min
// 15 min era muito agressivo: cada tab switch após almoço/reunião causava
// reset completo de todas as queries (~130MB por reset × 20 usuários × múltiplas vezes/dia)
const MIN_HIDDEN_TIME_MS = 60 * 60 * 1000;

export function useVisibilityRefresh() {
  const queryClient = useQueryClient();
  const hiddenAtRef = useRef<number | null>(null);
  const isRefreshingRef = useRef(false);

  // Usar cross-tab sync para verificar outras abas
  const { hasOtherVisibleTabs, getActiveTabCount } = useCrossTabSync();

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // Aba ficou oculta - registrar timestamp
      hiddenAtRef.current = Date.now();
    } else {
      // Aba voltou ao foco
      const hiddenAt = hiddenAtRef.current;

      if (hiddenAt && !isRefreshingRef.current) {
        const hiddenDuration = Date.now() - hiddenAt;

        // NOVO: Verificar se há outras abas visíveis
        const otherTabsVisible = hasOtherVisibleTabs();
        const activeTabCount = getActiveTabCount();

        // Log apenas se for significativo (> 5 min)
        if (hiddenDuration >= MIN_HIDDEN_TIME_MS) {
          console.warn(
            `👁️ [Visibility] Aba voltou ao foco após ${Math.round(hiddenDuration / 1000)}s`,
            `| Outras abas visíveis: ${otherTabsVisible}`,
            `| Total de abas ativas: ${activeTabCount}`
          );
        }

        if (hiddenDuration >= MIN_HIDDEN_TIME_MS) {
          // Se há outras abas visíveis, não resetar agressivamente
          if (otherTabsVisible) {
            console.log('[Visibility] Outras abas estão ativas - fazendo apenas invalidação suave');
            queryClient.invalidateQueries({
              predicate: (query) => {
                const key = query.queryKey[0];
                return typeof key === 'string' && (
                  key.startsWith('dashboard-') ||
                  key === 'financial-data' ||
                  key.startsWith('platform-')
                );
              }
            });
            hiddenAtRef.current = null;
            return;
          }

          isRefreshingRef.current = true;
          console.warn('🔄 [Visibility] Executando refresh seletivo após período oculto');

          // ⚠️ EGRESS FIX: em vez de resetar TUDO (causa ~130MB de egress),
          // apenas renovar sessão e invalidar suavemente.
          // Dados em cache continuam visíveis (placeholderData) enquanto recarregam.

          // PASSO 1: Renovar sessão Supabase (evita token expirado)
          supabase.auth.refreshSession()
            .then(() => {
              console.log('[Visibility] Sessão Supabase refreshada com sucesso');
            })
            .catch((err) => {
              console.warn('[Visibility] Erro ao refreshar sessão Supabase:', err);
            })
            .finally(async () => {
              // PASSO 2: Cancelar queries em andamento que podem estar travadas
              await queryClient.cancelQueries();

              // PASSO 3: Invalidar (NÃO resetar) apenas queries críticas do dashboard
              // invalidateQueries marca como stale mas NÃO força loading state
              // Dados anteriores continuam visíveis enquanto refetch acontece em background
              await queryClient.invalidateQueries({
                predicate: (query) => {
                  const key = query.queryKey[0];
                  return typeof key === 'string' && (
                    key.startsWith('dashboard-') ||
                    key === 'financial-data'
                  );
                }
              });

              // NÃO invalidar orbt-*, clickbank-*, cartpanda-*, platform-*
              // Esses dados são atualizados pelo staleTime normal (15 min)
              // Antes invalidava ~15 query groups, agora apenas 2 patterns

              console.warn('✅ [Visibility] Invalidação seletiva concluída');
              isRefreshingRef.current = false;
            });
        }
      }

      hiddenAtRef.current = null;
    }
  }, [queryClient, hasOtherVisibleTabs, getActiveTabCount]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);
}
